/**
 * StaffCortex — IT-SCE Intern: Cortex-Token-Verbrauch aller Tenants.
 *
 * TQ #9698 (Staff-Migration Teil E), portiert von
 * customer-portal/src/pages/staff/StaffCortex.tsx. Backend portal-api
 * GET /api/staff/cortex/usage (services/api.ts staffGetCortexUsage,
 * proxied zu Cortex admin-API via CORTEX_ADMIN_TOKEN service-token).
 * Read-only Uebersicht — kein Schreibzugriff auf Cortex.
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { StaffCortexUsage } from '../services/api'
import { useToast } from '../components/ui'

function fmtEUR(n: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 }).format(n)
}
function fmtNum(n: number) {
  return new Intl.NumberFormat('de-DE').format(n)
}

export default function StaffCortex() {
  const [data, setData] = useState<StaffCortexUsage | null>(null)
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const resp = await api.staffGetCortexUsage()
        if (!cancelled) setData(resp)
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
        showToast('error', `Laden fehlgeschlagen: ${msg}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    const t = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>🧠 Cortex-Übersicht</h1>
        <p className="page-subtitle">Token-Verbrauch aller Tenants (letzte 30 Tage, Auto-Refresh 60s)</p>
      </div>

      {loading && !data ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade Cortex-Daten…</div>
      ) : !data ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Daten verfügbar.</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Card label="Events (30d)" value={fmtNum(data.total_events)} />
            <Card label="Tokens In" value={fmtNum(data.total_tokens_in)} />
            <Card label="Tokens Out" value={fmtNum(data.total_tokens_out)} />
            <Card label="Kosten gesamt" value={fmtEUR(data.total_cost_eur)} highlight={data.total_cost_eur > 0 ? '#92400e' : undefined} />
            <Card label="Top-Up Restbestand" value={fmtNum(data.total_topup_remaining)} />
            <Card label="Tenants aktiv" value={String(data.tenants.length)} />
          </div>

          <h2 style={{ fontSize: 18, marginTop: 24, marginBottom: 12 }}>Top-Verbraucher</h2>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <Th>Tenant</Th>
                  <Th align="right">Events</Th>
                  <Th align="right">Tokens In</Th>
                  <Th align="right">Tokens Out</Th>
                  <Th align="right">Kosten</Th>
                  <Th>Letzter Event</Th>
                </tr>
              </thead>
              <tbody>
                {data.tenants.map(t => (
                  <tr key={t.tenant} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <Td><code>{t.tenant}</code></Td>
                    <Td align="right">{fmtNum(t.events)}</Td>
                    <Td align="right">{fmtNum(t.tokens_in)}</Td>
                    <Td align="right">{fmtNum(t.tokens_out)}</Td>
                    <Td align="right"><strong>{fmtEUR(t.cost_eur)}</strong></Td>
                    <Td>{t.last_event_at ? new Date(t.last_event_at).toLocaleString('de-DE') : '—'}</Td>
                  </tr>
                ))}
                {data.tenants.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Aktivität in den letzten 30 Tagen.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Card({ label, value, highlight }: { label: string; value: string; highlight?: string }) {
  return (
    <div style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ color: highlight ?? '#111827', fontSize: 20, fontWeight: 600 }}>{value}</div>
    </div>
  )
}
function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th style={{ padding: '10px 12px', textAlign: align ?? 'left', fontWeight: 600 }}>{children}</th>
}
function Td({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ padding: '10px 12px', textAlign: align ?? 'left' }}>{children}</td>
}
